import React, { FC, useEffect, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { container } from 'src/inversify.config';
import { Select, Spin, Tag } from 'antd';
import debounce from 'lodash/debounce';
import { Query } from 'src/core/stores/data-store';
import { RoleSummaryStore, RoleSummary } from 'src/stores/roles-store';
import { Selector } from 'src/common/types';
import { generateSelectorOdataFilter, getRoleDisplayName } from 'src/common/utils';
import { ImmutableObject } from '@hookstate/core';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';

const tagRender = (props: CustomTagProps) => {
  const { label, closable, onClose } = props;
  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <Tag onMouseDown={onPreventMouseDown} closable={closable} onClose={onClose}>
      {getRoleDisplayName(`${label}`)}
    </Tag>
  );
};

const RolesSelect: FC<
  {
    onChange?: (values: { value: string; label: string; selector: Selector }[]) => void;
    exclude?: string[];
    value?: { value: string; label: string }[];
    organizationId?: string;
    minSelector?: Selector;
    maxSelector?: Selector;
    editRoles?: boolean;
    excludeNames?: string[];
    filterNames?: string[];
  } & WithTranslation
> = ({
  t,
  onChange,
  exclude,
  value,
  organizationId,
  minSelector = Selector.Drillhole,
  maxSelector = Selector.Partners,
  editRoles = false,
  filterNames,
  excludeNames,
}) => {
  const roleStore = container.get(RoleSummaryStore);
  const rolesState = roleStore.state;
  const [currentValues, setValues] = useState((value || []).map((x) => ({ value: x.value, label: x.label || `Role ${x.value}` })));

  const load = (search: string | undefined = undefined) => {
    let filter = [generateSelectorOdataFilter(minSelector, maxSelector)];

    if (organizationId != null) filter.push(`OrganizationId eq ${organizationId}`);

    if ((exclude || []).length !== 0) filter = [...filter, `id ne ${exclude?.join(' and id ne ')}`];

    const nameFilterArray = filterNames?.map((name) => `Name eq '${name}'`);
    const nameFilter = `(${nameFilterArray?.join(' or ')})`;
    if (filterNames) filter = [...filter, nameFilter];

    const excludeNamesFilterArray = excludeNames?.map((name) => `Name ne '${name}'`);
    const excludeNamesFilter = `(${excludeNamesFilterArray?.join(' and ')})`;
    if (excludeNames) filter = [...filter, excludeNamesFilter];

    roleStore.load({
      searchQuery: search,
      orderBy: [{ field: 'name', direction: 'Ascending', useProfile: false }],
      skip: 0,
      take: 1000000,
      filter,
    } as Query);
  };

  useEffect(() => {
    if (editRoles) {
      load('');
      return;
    }
    setValues([]);
    load('');
    if (currentValues.length !== 0) {
      onChange && onChange([]);
    }
  }, [organizationId, minSelector, maxSelector]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = debounce((value: string) => {
    load(value);
  }, 800);

  const onSelect = (nextValues: { value: string; label: string }[], options: any) => {
    setValues(nextValues);
    onChange && onChange((options as any[]).map((x) => ({ value: x.value, label: x.label, selector: x.selector })));
  };

  return (
    <>
      <Select
        className="role-select"
        optionLabelProp="label"
        tagRender={tagRender}
        mode="multiple"
        loading={!!rolesState.isBusy.get()}
        labelInValue
        placeholder={t('SelectRoles')}
        onChange={onSelect}
        style={{ width: '100%' }}
        disabled={!organizationId}
        allowClear
        showSearch
        value={currentValues}
        notFoundContent={rolesState.isBusy.get() ? <Spin size="small" /> : undefined}
        filterOption={false}
        showArrow={false}
        onSearch={onSearch}
        onDropdownVisibleChange={(visible) => {
          if (!visible) load();
        }}
      >
        {(rolesState.items.value || []).map((o: ImmutableObject<RoleSummary>) => (
          <Select.Option key={o.id} value={o.id} selector={o.selector} label={o.name}>
            {getRoleDisplayName(o.name)}
          </Select.Option>
        ))}
      </Select>
    </>
  );
};

export default withTranslation()(RolesSelect);
